import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useProductsStore } from '@/stores/products'

// Route names for type safety
export const ROUTE_NAMES = {
  HOME: 'Home',
  DESIGNERS: 'Designers',
  DESIGNER_DETAIL: 'DesignerDetail',
  SELECTIONS: 'Selections',
  SELECTION_DETAIL: 'SelectionDetail',
  BLOG: 'Blog',
  BLOG_ARTICLE: 'BlogArticle',
  STORE: 'Store',
  PRODUCT_DETAIL: 'ProductDetail',
  CART: 'Cart',
  MEMBERSHIP: 'Membership',
  TERMS: 'Terms',
  SHIPPING: 'Shipping',
  COOKIE_POLICY: 'CookiePolicy'
} as const

// Lazy load components for better performance
const HomeView = () => import('@/views/HomeView.vue')
const DesignersView = () => import('@/views/DesignersView.vue')
const DesignerDetail = () => import('@/views/DesignerDetail.vue')
const SelectionsView = () => import('@/views/SelectionsView.vue')
const SelectionDetail = () => import('@/views/SelectionDetail.vue')
const BlogView = () => import('@/views/BlogView.vue')
const BlogArticleView = () => import('@/views/BlogArticleView.vue')
const CatalogView = () => import('@/views/CatalogView.vue')
const ProductDetail = () => import('@/views/ProductDetail.vue')
const Cart = () => import('@/views/Cart.vue')
const MembershipView = () => import('@/views/MembershipView.vue')
const TermsView = () => import('@/views/TermsView.vue')
const ShippingView = () => import('@/views/ShippingView.vue')
const CookieView = () => import('@/views/CookieView.vue')

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: ROUTE_NAMES.HOME,
    component: HomeView,
    meta: {
      title: 'VY Online - Home',
      description: 'Welcome to VY Online'
    }
  },
  {
    path: '/designers',
    name: ROUTE_NAMES.DESIGNERS,
    component: DesignersView,
    meta: {
      title: 'Designers - VY Online',
      description: 'Explore our talented designers'
    }
  },
  {
    path: '/designers/:name',
    name: ROUTE_NAMES.DESIGNER_DETAIL,
    component: DesignerDetail,
    props: true,
    meta: {
      title: 'Designer Details - VY Online',
      description: 'Learn more about the designer'
    }
  },
  {
    path: '/selections',
    name: ROUTE_NAMES.SELECTIONS,
    component: SelectionsView,
    meta: {
      title: 'Selections - VY Online',
      description: 'Curated selections'
    }
  },
  {
    path: '/selections/:id(\\d+)',
    name: ROUTE_NAMES.SELECTION_DETAIL,
    component: SelectionDetail,
    props: route => ({ id: Number(route.params.id) }),
    meta: {
      title: 'Selection - VY Online',
      description: 'View curated selection details'
    }
  },
  {
    path: '/blog',
    name: ROUTE_NAMES.BLOG,
    component: BlogView,
    meta: {
      title: 'Blog - VY Online',
      description: 'Latest news and updates'
    }
  },
  {
    path: '/blog/:id(\\d+)',
    name: ROUTE_NAMES.BLOG_ARTICLE,
    component: BlogArticleView,
    props: route => ({ id: Number(route.params.id) }),
    meta: {
      title: 'Article - VY Online',
      description: 'Read the full article'
    }
  },
  {
    path: '/store',
    name: ROUTE_NAMES.STORE,
    component: CatalogView,
    meta: {
      title: 'Store - VY Online',
      description: 'Browse our product catalog'
    }
  },
  {
    path: '/product/:id(\\d+)',
    name: ROUTE_NAMES.PRODUCT_DETAIL,
    component: ProductDetail,
    props: route => ({ id: Number(route.params.id) }),
    meta: {
      title: 'Product Details - VY Online',
      description: 'View product details'
    }
  },
  {
    path: '/cart',
    name: ROUTE_NAMES.CART,
    component: Cart,
    meta: {
      title: 'Shopping Cart - VY Online',
      description: 'Review your cart items'
    }
  },
  {
    path: '/membership',
    name: ROUTE_NAMES.MEMBERSHIP,
    component: MembershipView,
    meta: {
      title: 'Membership - VY Online',
      description: 'Join our membership program'
    }
  },
  {
    path: '/terms',
    name: ROUTE_NAMES.TERMS,
    component: TermsView,
    meta: {
      title: 'Terms of Service - VY Online',
      description: 'Terms and conditions'
    }
  },
  {
    path: '/shipping',
    name: ROUTE_NAMES.SHIPPING,
    component: ShippingView,
    meta: {
      title: 'Shipping Info - VY Online',
      description: 'Shipping information and policies'
    }
  },
  {
    path: '/cookie-policy',
    name: ROUTE_NAMES.COOKIE_POLICY,
    component: CookieView,
    meta: {
      title: 'Cookie Policy - VY Online',
      description: 'Our cookie usage policy'
    }
  },
  // Legacy routes for backwards compatibility
  {
    path: '/basket',
    redirect: '/cart'
  },
  // Catch all 404 - must be last
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('@/views/NotFound.vue'),
    meta: {
      title: 'Page Not Found - VY Online',
      description: 'The page you are looking for does not exist'
    }
  }
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Global navigation guards
router.beforeEach((to, from, next) => {
  // Update document title
  if (to.meta?.title) {
    document.title = to.meta.title as string
  }

  // Update meta description
  const descriptionMeta = document.querySelector('meta[name="description"]')
  if (to.meta?.description && descriptionMeta) {
    descriptionMeta.setAttribute('content', to.meta.description as string)
  }

  next()
})

// Error handling for route loading
router.onError(error => {
  console.error('Router error:', error)
})

export default router
